/**
 * Backend response envelope.
 *
 * Every controller returns `ResponseConverter.convert(service.decorate(...))`
 * which serializes a CommandResult / QueryResult with this exact shape.
 * The HTTP status is taken from `messages[0].httpStatus` server-side, so a
 * 4xx/5xx response *still* carries this body — never assume errors are bare
 * strings.
 */
export type MessageType = 'INFO' | 'WARN' | 'ERROR' | 'SUCCESS';

export interface InternalMessage {
  code: string;
  message: string;
  type: MessageType;
  httpStatus?: string;
}

export interface ResponseEnvelope<T> {
  success: boolean;
  messages: InternalMessage[];
  data: T | null;
}

/** Thrown by the api client when `success === false`. */
export class ApiError extends Error {
  readonly status: number;
  readonly messages: InternalMessage[];

  constructor(status: number, messages: InternalMessage[]) {
    super(messages[0]?.message ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.messages = messages;
  }

  /** Convenience: pull the first error-typed message for toasts. */
  get firstErrorMessage(): string {
    const err = this.messages.find((m) => m.type === 'ERROR');
    return err?.message ?? this.message;
  }
}
